import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { RootState } from "@/redux/store";

/* ============================
   Types
============================ */

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  mobileNumber?: string;
  className?: string;
  employeeType?: "teaching" | "nonTeaching";
  jobShift?: string;
  grossSalary?: string;
  gender?: "male" | "female" | "other" | "prefer_not_to_say";
  roleId?: string;
  roleIds?: string[];
  roles?: Array<{ id: string; name: string }>;
  role: string;
  status: "active" | "inactive";
  lastLogin: string;
  createdAt: string;
}

interface UserState {
  users: UserProfile[];
  loading: boolean;
  error: string | null;
}

/* ============================
   Runtime guards
============================ */

/**
 * Ensures runtime data never breaks UI assumptions.
 * Prevents object → string crashes (your original issue).
 */
function sanitizeUserProfile(user: UserProfile): UserProfile {
  return {
    ...user,
    avatar: typeof user.avatar === "string" ? user.avatar : undefined,
  };
}

/**
 * Centralized error normalizer.
 * ONLY strings ever enter Redux state.
 */
function toErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  return "Something went wrong";
}

/* ============================
   Initial state
============================ */

const initialState: UserState = {
  users: [],
  loading: false,
  error: null,
};

/* ============================
   Slice
============================ */

const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    setUsers(state, action: PayloadAction<UserProfile[]>) {
      state.users = action.payload.map(sanitizeUserProfile);
      state.error = null; // reset stale errors
    },

    addUser(state, action: PayloadAction<UserProfile>) {
      state.users.push(sanitizeUserProfile(action.payload));
    },

    updateUser(state, action: PayloadAction<UserProfile>) {
      const index = state.users.findIndex((u) => u.id === action.payload.id);
      if (index !== -1) {
        state.users[index] = sanitizeUserProfile(action.payload);
      }
    },

    deleteUser(state, action: PayloadAction<string>) {
      state.users = state.users.filter((u) => u.id !== action.payload);
    },

    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },

    /**
     * STRICTLY typed.
     * Impossible to dispatch objects at compile-time.
     */
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },

    /**
     * Optional but recommended:
     * lets thunks safely forward unknown errors
     */
    setErrorFromUnknown(state, action: PayloadAction<unknown>) {
      state.error = toErrorMessage(action.payload);
    },

    clearError(state) {
      state.error = null;
    },
  },
});

export const {
  setUsers,
  addUser,
  updateUser,
  deleteUser,
  setLoading,
  setError,
  setErrorFromUnknown,
  clearError,
} = userSlice.actions;

export default userSlice.reducer;

/* ============================
   Selectors
============================ */

export const selectUsers = (state: RootState) => state.users.users;
export const selectUsersLoading = (state: RootState) => state.users.loading;
export const selectUsersError = (state: RootState) => state.users.error;

export const selectUserById = (userId: string | undefined) => (state: RootState) =>
  userId ? state.users.users.find((u) => u.id === userId) : undefined;
